import { Button, Input } from '@/components';
import { TextMessage } from '@/constant';
import { cn, getFieldInputProps, showAlert, showToast } from '@/utils';
import { memo, useMemo } from 'react';
import { Address, useDaumPostcodePopup } from 'react-daum-postcode';

import { FormikProps } from 'formik';

import { height, left, top, width } from './config';

export interface IOutputAddress {
	zipcode: string;
	address: string;
}
interface IProps<T> {
	onCompleteSearch: ({ zipcode, address }: IOutputAddress) => void;
	formik: FormikProps<T>;
	isDetail?: boolean;
	containerClassName?: string;
	alwaysShowDeleteButton?: boolean;
}

const PostCodePopup = <
	T extends { zipcode: string; address: string; addressDetail: string },
>({
	onCompleteSearch,
	formik,
	isDetail = false,
	containerClassName,
	alwaysShowDeleteButton = false,
}: IProps<T>) => {
	const open = useDaumPostcodePopup();

	const hasAddressData = useMemo(
		() =>
			!!formik?.values.zipcode ||
			!!formik?.values.address ||
			!!formik?.values.addressDetail,
		[
			formik?.values.zipcode,
			formik?.values.address,
			formik?.values.addressDetail,
		]
	);

	const handleCompleteSearchAddress = (data: Address) => {
		onCompleteSearch({
			zipcode: data?.zonecode,
			address: data?.address,
		});
	};

	const handleOpenPostcodePopup = () => {
		open({
			onComplete: handleCompleteSearchAddress,
			width,
			height,
			top,
			left,
		});
	};

	const handleResetAddress = () => {
		showAlert(
			TextMessage.CONFIRM_25,
			() => {
				formik.setValues((pre) => ({
					...pre,
					zipcode: '',
					address: '',
					addressDetail: '',
				}));

				if (isDetail) {
					setTimeout(() => {
						formik.handleSubmit();
					}, 100);
				}

				showToast(TextMessage.DELETE_SUCCESS);
			},
			() => {}
		);
	};

	return (
		<div
			className={cn('flex flex-col flex-1 gap-1.5 w-full', containerClassName)}
		>
			<div className="flex items-center gap-1.5 w-full">
				<Input
					disabled
					containerClassName="flex-1"
					{...getFieldInputProps(formik, 'zipcode')}
				/>

				<Button secondary onClick={handleOpenPostcodePopup}>
					우편번호
				</Button>
				{(alwaysShowDeleteButton || formik?.values.zipcode) && (
					<Button onClick={handleResetAddress} disabled={!hasAddressData}>
						삭제
					</Button>
				)}
			</div>

			<Input
				placeholder="주소를 입력해주세요."
				{...getFieldInputProps(formik, 'address')}
				maxLength={100}
				isDetail={isDetail}
			/>
			<Input
				placeholder="상세 주소를 입력해주세요."
				{...getFieldInputProps(formik, 'addressDetail')}
				maxLength={100}
				isDetail={isDetail}
			/>
		</div>
	);
};

export default memo(PostCodePopup) as typeof PostCodePopup;
